import AppSDK from "@growthos/ui-app-sdk";
import AppDispatcher from "../Dispatcher";
import { AppActionConstants } from "./AppActionConstants";
import AppConfig from "../../AppConfig";
import { fetchWithAuthGet, fetchWithAuthPost } from "../FetchWithAuth";
import BookFiltersStore from "../Stores/BookFiltersStore";
import MasterCampaignsStore from "../Stores/MasterCampaignsStore";
import RequestHelpers from "../../Helpers/RequestHelpers";

import { inferDateRangeFromFilterPresets } from "../../Views/ViewMasterCampaigns/utils";

// Helper to format search/filters/sort params for campaign queries
export function buildCampaignsQueryParams({ search = "", filters = {}, sortProp = null, sortDir = null, pageNumber = 1, pageSize = 10 } = {}) {
	// Remove the `overview-filter-` or `archive-filter-` prefix from the filter keys
	const newFilters = Object.entries(filters).reduce((acc, [key, value]) => {
		if (key.startsWith("overview-filter-") || key.startsWith("archive-filter-")) {
			const newKey = key.replace(/^(overview-filter-|archive-filter-)/, "");
			acc[newKey] = value;
		} else {
			acc[key] = value;
		}
		return acc;
	}, {});

	// Convert date.customDate[] to two properties: startDate and endDate
	const [startDate, endDate] = inferDateRangeFromFilterPresets(newFilters?.date);
	if (startDate) newFilters.startDate = startDate;
	if (endDate) newFilters.endDate = endDate;
	delete newFilters.date;

	// Format keyValueFilter how the endpoint wants it
	if (newFilters.keyValueFilter) {
		newFilters.keyValueFilter = Object.entries(newFilters.keyValueFilter)?.map(([DataKeyID, DataValueID]) => ({ DataKeyID: parseInt(DataKeyID), DataValueID }));
	}

	// Format platform-name how the endpoint wants it
	if (newFilters["platform-name"]) {
		newFilters.PlatformID = Object.entries(newFilters["platform-name"])
			?.filter(([PlatformID, value]) => value === true)
			?.map(([PlatformID]) => parseInt(PlatformID));
		delete newFilters["platform-name"];
	}

	// Format draftStatus how the endpoint wants it
	if (newFilters.draftStatus) {
		const selected = Object.entries(newFilters.draftStatus)
			?.filter(([_, v]) => v)
			?.map(([key]) => key);
		if (selected.length === 1) {
			newFilters.draftStatus = selected[0] === "draft";
		} else {
			delete newFilters.draftStatus;
		}
	}

	const DIRECTION_MAP = {
		asc: "ASC",
		dsc: "DESC"
	};

	return {
		pageNumber,
		pageSize,
		search,
		filters: JSON.stringify(newFilters),
		sortBy: sortProp,
		sortOrder: DIRECTION_MAP?.[sortDir] || null
	};
}

const book = AppConfig.getBookApiBasePath();

export const fetchConfigAndPlatforms = () => {
	const contextValues = BookFiltersStore.getContextValues();
	return Promise.all([
		fetchWithAuthGet(`${book.ApiBasePath}/plook/PlatformLevelConfigurationViewModel/GetPlatformLevelConfig/-1/-1`),
		fetchWithAuthGet(`${book.ApiBasePath}/plook/MasterPlatformView/GetPlatforms/${contextValues.contextId}`)
	])
		.then((response) => {
			const config = response[0];
			const mediaChannelPlatforms = response[1];
			const campaignData = {
				config,
				mediaChannelPlatforms
			};
			AppDispatcher.dispatch({
				type: AppActionConstants.fetchConfigAndPlatforms, // "FETCHCAMPAIGNS"
				value: campaignData
			});
			return campaignData;
		})
		.catch((error) => {
			console.log(error);
		});
};

export const fetchCampaignsByPage = ({
	// this comment exists to prevent eslint from making this unreadable.
	page: pageNumber = 1,
	rowsPerPage: pageSize = 10,
	archive = false,
	search = "",
	filters = {},
	sortProp = null,
	sortDir = null,
	abortController = null
} = {}) => {
	const { contextId } = BookFiltersStore.getContextValues();
	const params = new URLSearchParams(buildCampaignsQueryParams({ search, filters, sortProp, sortDir, pageNumber, pageSize }));
	const url = `${book.ApiBasePath}/plook/MasterCampaign/GetMasterCampaigns/${contextId}/${archive ? "2" : "1"}?${params.toString()}`;
	return fetchWithAuthGet(url, { abortController }).then(({ data, totalPrimaryCampaignsCount, totalActivationCampaignsCount, showDraftStatusColumn }) => {
		// If we cloned a campaign, we want to sort the destination master campaign to the top of the table
		const targetMasterCampaignID = MasterCampaignsStore.getTargetMasterCampaignID();
		if (targetMasterCampaignID) {
			const destinationMasterCampaign = data.find((campaign) => {
				return campaign.MasterCampaignID == targetMasterCampaignID;
			});
			if (destinationMasterCampaign) {
				data.splice(data.indexOf(destinationMasterCampaign), 1);
				data.unshift(destinationMasterCampaign);
			}
		}
		if (archive) {
			data = data.filter(({ IsArchived, ArchivedCampaigns }) => {
				return IsArchived || Object.values(ArchivedCampaigns).some((platformCampaigns) => platformCampaigns.length > 0);
			});
		} else {
			data = data.filter(({ ActivationCampaigns, IsArchived }) => {
				return !IsArchived || Object.values(ActivationCampaigns).some((platformCampaigns) => platformCampaigns.length > 0);
			});
		}
		return { data, totalPrimaryCampaignsCount, totalActivationCampaignsCount, showDraftStatusColumn };
	});
};

// Example usage: downloadCampaigns({ search, filters })
export const downloadCampaigns = async ({ search = "", filters = {}, sortProp = null, sortDir = null, pageNumber = 1, pageSize = 10, selectAllState, checkboxSelections } = {}) => {
	const response = await RequestHelpers.doRequest({
		method: "POST",
		uri: `plook/ExcelReport/GetExportsForCampaigns`,
		params: {
			...buildCampaignsQueryParams({ search, filters, sortProp, sortDir, pageNumber, pageSize }),
			selectAll: selectAllState
		},
		body: JSON.stringify({ CampaignID: checkboxSelections?.selectedActivationCampaigns?.map(({ activationCampaignId }) => activationCampaignId) || [] }),
		headers: { "Content-Type": "application/json;charset=UTF-8" }
	});
	return response;
};

export const getCampaignDownloads = () => {
	return RequestHelpers.doRequest({
		method: "GET",
		uri: `plook/ExcelReport/GetCampaignDownloadLog`,
		headers: { "Content-Type": "application/json;charset=UTF-8" }
	});
};

export const downloadCampaignExport = async (campaignDownloadLogID) => {
	const response = await RequestHelpers.doRequest({
		method: "GET",
		uri: `plook/ExcelReport/DownloadCampaignFile`,
		params: { campaignDownloadLogID }
	});
	await RequestHelpers.downloadFile(response);
};

export const fetchCampaigns = (archive = false) => {
	const contextValues = BookFiltersStore.getContextValues();
	return Promise.all([
		fetchWithAuthGet(`${book.ApiBasePath}/plook/MasterCampaign/GetMasterCampaigns/${contextValues.contextId}/${archive ? "3" : "1"}`),
		fetchWithAuthGet(`${book.ApiBasePath}/plook/PlatformLevelConfigurationViewModel/GetPlatformLevelConfig/-1/-1`),
		fetchWithAuthGet(`${book.ApiBasePath}/plook/MasterPlatformView/GetPlatforms/${contextValues.contextId}`)
	]).then((response) => {
		const data = response[0];
		// If we cloned a campaign, we want to sort the destination master campaign to the top of the table
		const targetMasterCampaignID = MasterCampaignsStore.getTargetMasterCampaignID();
		if (targetMasterCampaignID) {
			const destinationMasterCampaign = data.find((campaign) => {
				return campaign.MasterCampaignID == targetMasterCampaignID;
			});
			data.splice(data.indexOf(destinationMasterCampaign), 1);
			data.unshift(destinationMasterCampaign);
		}
		const config = response[1];
		const mediaChannelPlatforms = response[2];
		const campaignData = {
			data,
			config,
			mediaChannelPlatforms
		};
		AppDispatcher.dispatch({
			type: AppActionConstants.fetchCampaigns, // "FETCHCAMPAIGNS"
			value: campaignData
		});
	});
};

export const fetchCampaignsOnWorkbookPage = (masterCampaignID) => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(`${book.ApiBasePath}/plook/MasterCampaign/GetMasterCampaigns/${contextValues.contextId}/1/${masterCampaignID}`).then((data) => {
		AppDispatcher.dispatch({
			type: AppActionConstants.fetchCampaigns, // "FETCHCAMPAIGNS"
			value: { data }
		});
	});
};

export const fetchCampaignsByPlatformId = (campaignData) => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/MasterCampaign/GetMasterCampaignsByPlatformId/${contextValues.contextId}/${campaignData?.MasterPlatformID}/${campaignData?.CampaignID}`
	);
};

export const fetchAdvertiserTaxonomy = (platforms) => {
	const contextValues = BookFiltersStore.getContextValues();
	const advertiserTaxonomies = {};
	return Promise.all(
		platforms.map((platform) =>
			fetchWithAuthGet(
				`${book.ApiBasePath}/plook/TaxonomyConfiguration/GetHeaders/${contextValues.contextId}/${contextValues?.agency?.name}/${contextValues?.client?.name}/${contextValues?.region?.name}/${contextValues?.country?.name}/${platform.MasterPlatformID}/${platform.HierarchyLevelID}`
			)
				.then((response) => ({ name: platform.PlatformName, response }))
				.catch((e) => {
					console.error(`Error fetching advertiser taxonomy for platform ${platform.PlatformName}:`, e);
					return { name: platform.PlatformName, response: null };
				})
		)
	).then((results) => {
		results.forEach(({ name, response }) => {
			if (response != null) {
				advertiserTaxonomies[name] = response;
			}
		});
		AppDispatcher.dispatch({
			type: AppActionConstants.advertisersFetched,
			value: advertiserTaxonomies
		});
		return results;
	});
};

export const fetchActivationHeaders = (config, hierarchyLevelID) => {
	/* http://172.18.30.210/Book2API/custom/getheaders/6/827/3/1/4 */
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/TaxonomyConfiguration/GetHeaders/${contextValues.contextId}/${contextValues?.agency?.name}/${contextValues?.client?.name}/${contextValues?.region?.name}/${contextValues?.country?.name}/${config.platform.value}/${hierarchyLevelID}`
	)
		.then((response) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.fetchActivationHeaders, // "FETCHCAMPAIGNDATA"
				value: response
			});
		})
		.catch((error) => {
			console.log(error);
		});
};

export const putCampaignData = (data) => {
	/* API ENDPOINT http://172.18.30.210/Book2API/custom/InsertMasterCampaign */

	const postData = JSON.stringify(data);
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/MasterCampaignSaveViewModel/InsertUpdateMasterCampaign`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.updateCampaignDataSuccess,
				value: resp
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.updateCampaignDataFail, // "UPDATECAMPAIGNDATAFAIL"
				value: errorObject
			});
		});
};

export const editCampaignData = (data) => {
	/* API ENDPOINT http://172.18.30.210/Book2API/custom/InsertMasterCampaign */

	const postData = JSON.stringify(data);
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/MasterCampaignSaveViewModel/InsertUpdateMasterCampaign`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.editCampaignDataSuccess, // "EDITCAMPAIGNSUCCESS"
				value: resp
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.editCampaignDataFail, // "EDITCAMPAIGNFAIL"
				value: errorObject
			});
		});
};

export const putActivationData = (data) => {
	/* API ENDPOINT /Book2API/custom/InsertMasterCampaign */

	const user = AppSDK.ApiUser.getUser();
	data.FirstName = user?.givenName;
	data.LastName = user?.familyName;
	data.Email = user?.userName;

	const postData = JSON.stringify(data);
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/CampaignSaveViewModel/InsertUpdateCampaign`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.updateActivationDataSuccess, // "SAVE"
				value: resp
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.updateActivationDataFail, // "FAILEDSUBMIT"
				value: errorObject
			});
		});
};

export const editActivationData = (data, callback) => {
	/* API ENDPOINT /Book2API/custom/InsertMasterCampaign */

	const user = AppSDK.ApiUser.getUser();
	data.FirstName = user?.givenName;
	data.LastName = user?.familyName;
	data.Email = user?.userName;

	const postData = JSON.stringify(data);

	return fetchWithAuthPost(`${book.ApiBasePath}/plook/CampaignSaveViewModel/InsertUpdateCampaign`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.editActivationDataSuccess, // "EDITACTIVATIONDATASUCCESS"
				value: resp
			});
			callback?.();
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.editActivationDataFail, // "EDITACTIVATIONDATAFAIL"
				value: errorObject
			});
		});
};

export const createAdvertiser = (data) => {
	const postData = JSON.stringify(data[1]);
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/CampaignSaveViewModel/InsertUpdateAdvertiser`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.advertiserCreatedSuccess,
				value: resp,
				advertiser: data[0]
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.advertiserCreatedFail,
				value: errorObject,
				advertiser: data[0]
			});
		});
};

export const createAdvertisers = async (postObjects, type = "create") => {
	try {
		const requests = postObjects.map((data) => fetchWithAuthPost(`${book.ApiBasePath}/plook/CampaignSaveViewModel/InsertUpdateAdvertiser`, JSON.stringify(data[1])));
		const results = await Promise.allSettled(requests);

		const errors = {};
		const successfulAdvertisers = [];

		for (const result of results) {
			if (result.status === "rejected") {
				const index = results.indexOf(result);
				const advertiserName = postObjects[index][0];
				errors[advertiserName] = await result.reason.details.json();
			}

			if (result.status === "fulfilled") {
				const index = results.indexOf(result);
				const advertiserName = postObjects[index][0];
				delete errors.advertiserName;
				successfulAdvertisers.push(advertiserName);
			}
		}

		if (Object.keys(errors).length > 0) {
			AppDispatcher.dispatch({
				type: type === "update" ? AppActionConstants.advertiserUpdatedBulkFail : AppActionConstants.advertiserCreatedBulkFail,
				value: errors
			});
		}

		if (successfulAdvertisers.length > 0) {
			AppDispatcher.dispatch({
				type: type === "update" ? AppActionConstants.advertisersUpdatedSuccess : AppActionConstants.advertisersCreatedSuccess,
				advertisers: successfulAdvertisers
			});
		}

		return { values: successfulAdvertisers, errors };
	} catch (error) {
		// a non-network error occurred
		console.log(error);
	}
};

export const updateAdvertiser = (data) => {
	const postData = JSON.stringify(data[1]);
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/CampaignSaveViewModel/InsertUpdateAdvertiser`, postData)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.advertiserUpdatedSuccess,
				value: resp,
				advertiser: data[0]
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.advertiserUpdatedFail,
				value: errorObject,
				advertiser: data[0]
			});
		});
};

export const holdMasterCampaignUniqueID = (ID) => {
	AppDispatcher.dispatch({
		type: AppActionConstants.holdMasterCampaignUniqueID,
		value: ID
	});
};

export const putCampaignUniqueID = (ID) => {
	AppDispatcher.dispatch({
		type: AppActionConstants.putCampaignUniqueID,
		value: ID
	});
};

export const getPlatformAdvertisers = (config) => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/Advertiser/GetPlatformAdvertisers/${contextValues.contextId}/${contextValues.agency.name}/${contextValues.client.name}/${contextValues.region.name}/${contextValues.country.name}/${config.masterPlatformID}`
	)
		.then((response) => {
			const advertisers = response.PlatformAdvertisers;
			const reformattedPlatformAdvertisers = advertisers
				?.filter((advertiser) => !advertiser.IsArchived)
				.map((obj) => {
					return { value: obj.PlatformAdvertiserID, label: obj.PlatformAdvertiserName };
				});
			AppDispatcher.dispatch({
				type: AppActionConstants.getPlatformAdvertisers,
				value: reformattedPlatformAdvertisers
			});
		})
		.catch((error) => {
			console.log(error);
		});
};

export const getPlatformAdvertisersGeneric = (config, callback) => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/Advertiser/GetPlatformAdvertisers/${contextValues.contextId}/${contextValues.agency.name}/${contextValues.client.name}/${contextValues.region.name}/${contextValues.country.name}/${config.masterPlatformID}`
	)
		.then((response) => {
			const advertisersResponse = { PlatformAdvertisers: [], FinancialSettings: {} };
			response?.PlatformAdvertisers?.filter((advertiser) => !advertiser.IsArchived)?.forEach((advertiser) => {
				advertisersResponse.PlatformAdvertisers.push({
					value: advertiser.PlatformAdvertiserID,
					label: advertiser.PlatformAdvertiserName,
					isPrismaMultiple: advertiser.isPrismaMultiple
				});
			});

			// If the platform is Prisma, we want to populate the financial settings for the campaign based on the response.
			if (config.masterPlatformID == 5) {
				const financialSettingNames = ["PrismaBuyTypeOptions", "PrismaClientCodeoptions", "PrismaMediaCodeOptions", "PrismaProductOptions"];
				if (financialSettingNames.every((name) => Array.isArray(response[name]) && response[name].length > 0)) {
					financialSettingNames.forEach((name) => {
						advertisersResponse.FinancialSettings[name] = response[name].map((setting) => {
							return { value: setting.DataValueCode, label: setting.DataValueShortDescr };
						});
					});
				}
			}

			callback(advertisersResponse);
		})
		.catch((error) => {
			console.log(error);
		});
};

export const getCampaignFinancialSettings = (activationCampaignID) => {
	return fetchWithAuthGet(`${book.ApiBasePath}/plook/Advertiser/GetCampaignFinancialSettings/${activationCampaignID}`).then((res) => {
		return res;
	});
};

/// plook/Campaign/AddCampaignToPrisma/31322{campaignid}/5063(advertiserid)
export const AddCampaignToPrisma = (activationCampaignID, advertiserID, financialSettings) => {
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/Campaign/AddCampaignToPrisma/${activationCampaignID}/${advertiserID}`, JSON.stringify(financialSettings))
		.then((res) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.addCampaignToPrismaSuccess,
				activationCampaignID,
				advertiserID,
				value: res,
				platform: "Mediaocean (OX)/Prisma"
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.addCampaignToPrismaFailure,
				activationCampaignID,
				advertiserID,
				value: errorObject,
				platform: "Mediaocean (OX)/Prisma"
			});
		});
};

export const AddCampaignToDV360 = (activationCampaignID, advertiserID) => {
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/Campaign/AddCampaignToDV360/${activationCampaignID}/${advertiserID}`)
		.then((res) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.addCampaignToDV360Success,
				activationCampaignID,
				advertiserID,
				value: res,
				platform: "DV360"
			});
		})
		.catch(async (error) => {
			const errorObject = await error.details.json();
			AppDispatcher.dispatch({
				type: AppActionConstants.addCampaignToDV360Failure,
				activationCampaignID,
				advertiserID,
				value: errorObject,
				platform: "DV360"
			});
		});
};

export const AddCampaignToGCM = (activationCampaignID, advertiserID) => {
	return fetchWithAuthGet(`${book.ApiBasePath}/GoogleProxy/SendCampaignToGcm?campaignID=${activationCampaignID}&advertiserId=${advertiserID}`);
};

export const GCMPostPushHandling = (success) => {
	return AppDispatcher.dispatch({
		type: AppActionConstants.addCampaignToGCM,
		value: success
	});
};

export const getDataKeyDataValueFilters = (hierarchyLevelName) => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/TaxonomyConfiguration/GetDataKeyDataValueFilters/${contextValues.contextId}/${contextValues.agency.name}/${contextValues.client.name}/${contextValues.region.name}/${contextValues.country.name}/${hierarchyLevelName}`
	)
		.then((response) => {
			const dataKeys = response.DataKey;
			dataKeys.forEach((dataKey) => {
				dataKey.label = dataKey.DataKeyName;
				dataKey.value = dataKey.DataKeyID;
				dataKey.DataValues.forEach((dataValue) => {
					dataValue.label = dataValue.DataValueShortDescr;
					dataValue.value = dataValue.DataValueID;
				});
			});
			AppDispatcher.dispatch({
				type: AppActionConstants.dataKeyDataValueFilters,
				value: dataKeys
			});
		})
		.catch((error) => {
			console.log(error);
		});
};

export const newArchiveCampaigns = (postData) => {
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/Campaign/ArchiveHierarchyLevel`, JSON.stringify(postData))
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.bulkArchiveCampaignSuccess,
				actionType: postData.ActionType
			});
		})
		.catch((error) => {
			console.log(error);
		});
};

export const copyCampaign = (activationCampaign, masterCampaignID, previousActivationCampaignIDs) => {
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/Campaign/CopyCampaign/${masterCampaignID}/${activationCampaign.CampaignID}`)
		.then((resp) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.copyCampaignSuccess,
				masterCampaignID,
				activationCampaignID: resp,
				previousActivationCampaignIDs
			});
		})
		.catch((error) => {
			console.log(error);
		});
};

export const createDraftCampaigns = (requests) => {
	return Promise.all(requests)
		.then((response) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.draftCampaignSuccess
			});
		})
		.catch((error) => {
			AppDispatcher.dispatch({
				type: AppActionConstants.draftCampaignFail
			});
		});
};

export const getBriefArtifactMappings = () => {
	const contextValues = BookFiltersStore.getContextValues();
	return fetchWithAuthGet(
		`${book.ApiBasePath}/plook/BriefArtifactMasterCampaignMapping/GetProcessedBriefsArtifacts/${contextValues.contextId}/${contextValues?.agency?.name}/${contextValues?.client?.name}/${contextValues?.region?.name}/${contextValues?.country?.name}`
	);
};

export const getCrossPlatformQAReport = (campaignId, platformIds) => {
	return fetchWithAuthPost(
		`${book.ApiBasePath}/plook/Placement/GetCrossPlatformQAReport`,
		JSON.stringify({
			CampaignID: campaignId,
			MasterPlatformIDs: platformIds
		})
	);
};

export const downloadCrossPlatformQAReport = (campaignId, platformIds) => {
	return RequestHelpers.doRequest({
		method: "POST",
		uri: `plook/Placement/DownloadCrossPlatformQAReport`,
		body: JSON.stringify({ CampaignID: campaignId, MasterPlatformIDs: platformIds }),
		headers: { "Content-Type": "application/json;charset=UTF-8" }
	}).then(RequestHelpers.downloadFile);
};

export const toggleCampaignBudgetLocked = (campaignId, locked) => {
	// QPs: /campaignid/islocked/isfromWorkbook
	return fetchWithAuthPost(`${book.ApiBasePath}/plook/Campaign/LockUnlockCampaignPlacements/${campaignId}/${locked}/false`).then(
		AppDispatcher.dispatch({
			type: AppActionConstants.lockUnlockCampaignBudgetSuccess,
			value: locked
		})
	);
};

export const getPermissionedDataKeys = () => {
	return fetchWithAuthGet(`${book.ApiBasePath}/plook/MasterCampaign/GetPermissionedDataKeys/`);
};
